import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePersona } from '@/app/providers';
import type { Persona } from '@/shared/types';
import { useBrand } from './BrandContext';
import { useToast } from './toast/ToastContext';

// =========================================================================
// OEM+ personas — "VIEWING AS" in the top bar. Fork-owned (zone C).
//
// Why this is separate from the foundation's PersonaProvider: `Persona` in
// src/shared/types.ts is zone A and append-only across versions
// (Admin | Editor | Reviewer | Viewer). Those are capability lenses, not job
// titles. OEM+ needs six job titles, so this provider owns the OEM identity
// and syncs the matching foundation persona through `maps`, leaving the
// upstream union untouched.
//
// TODO(p1-merge): the six entries below are hand-ported from the demo's
// PERSONAS constant (OEM+_Platform_Demo.html line 2088). P1 lands the typed
// seed at src/versions/v1/shared/seed/personas.ts; when it does, import
// OEM_PERSONAS from there and delete this literal. `home` and `maps` are
// chrome concerns and stay here either way.
// =========================================================================

export type OemPersonaId = 'rel' | 'rsm' | 'tech' | 'parts' | 'planner' | 'aftermarket';

export type OemPersona = {
  id: OemPersonaId;
  /** Job title shown in the switcher. */
  name: string;
  /**
   * Organisation line. `{brand}` is substituted with the active client's
   * lockup, so a Komatsu demo never shows "Meridian Equipment" in the
   * persona menu. Customer-side orgs are literal — they belong to the mine,
   * not the OEM.
   */
  org: string;
  /** Mine code this persona is posted to, or null for fleet-wide roles. */
  mine: string | null;
  /** Route the switcher navigates to on selection. */
  home: string;
  desc: string;
  /** The foundation capability lens this persona drives. */
  maps: Persona;
};

export const OEM_PERSONAS: OemPersona[] = [
  {
    id: 'rel',
    name: 'Fleet Reliability Lead',
    org: '{brand}',
    mine: null,
    home: '/',
    desc: 'Fleet-wide condition, trends, cost per hour, bulletins.',
    maps: 'Editor',
  },
  {
    id: 'rsm',
    name: 'Regional Service Manager',
    org: '{brand}',
    mine: null,
    home: '/',
    desc: 'Approvals, customer commitments, warranty, technician deployment.',
    maps: 'Admin',
  },
  {
    id: 'tech',
    name: 'Field Service Technician',
    org: '{brand} · Kalbarri',
    mine: 'KG',
    home: '/issues',
    desc: "Today's jobs, procedures, isolation steps, parts on the truck.",
    maps: 'Editor',
  },
  {
    id: 'parts',
    name: 'Parts & Logistics Coordinator',
    org: '{brand} · Perth DC',
    mine: null,
    home: '/approvals',
    desc: 'Stock below minimum, requisitions, freight options, lead times.',
    maps: 'Editor',
  },
  {
    id: 'planner',
    name: 'Site Maintenance Planner',
    org: 'Redgum · Mount Isolde (customer)',
    mine: 'MI',
    home: '/issues',
    desc: 'Shift plan, PM due, agent drafts to approve, restrictions.',
    maps: 'Reviewer',
  },
  {
    id: 'aftermarket',
    name: 'Aftermarket Manager',
    org: '{brand}',
    mine: null,
    home: '/',
    // fork(P7a/A3): rewritten in executive language. The old line was an
    // operational inventory ("modules, hotspots, technicians, customers,
    // parts and jobs"), and it renders verbatim as the `Today ·` subtitle on
    // the landing page — the first sentence an aftermarket executive reads.
    desc: 'Cost to serve across systems that never talk to each other, and where the fleet is drifting before it fails.',
    maps: 'Admin',
  },
];

export const OEM_PERSONA_BY_ID: Record<OemPersonaId, OemPersona> = Object.fromEntries(
  OEM_PERSONAS.map((p) => [p.id, p]),
) as Record<OemPersonaId, OemPersona>;

/** localStorage key. */
export const PERSONA_STORAGE_KEY = 'oem-plus:persona';

/**
 * Module-level default — deterministic for snapshots, like the brand default.
 *
 * fork(P7a/A3): `aftermarket`, not `rel`. The demo opened "VIEWING AS Fleet
 * Reliability Lead" to a room of aftermarket executives, which is the wrong
 * face and the wrong first sentence. Jay's own question 6 in
 * `docs/plans/simplification-pass.md` §4 asks for exactly this.
 */
export const DEFAULT_PERSONA: OemPersonaId = 'aftermarket';

/** Substitute the active client lockup into a persona's org line. */
export function orgLabel(persona: OemPersona, brandLockup: string): string {
  return persona.org.replace('{brand}', brandLockup);
}

function isPersonaId(value: unknown): value is OemPersonaId {
  return (
    value === 'rel' ||
    value === 'rsm' ||
    value === 'tech' ||
    value === 'parts' ||
    value === 'planner' ||
    value === 'aftermarket'
  );
}

function readStored(): OemPersonaId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PERSONA_STORAGE_KEY);
    return isPersonaId(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStored(id: OemPersonaId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PERSONA_STORAGE_KEY, id);
  } catch {
    // Storage unavailable — the choice just doesn't persist.
  }
}

type OemPersonaContextValue = {
  persona: OemPersona;
  personaId: OemPersonaId;
  personas: OemPersona[];
  /**
   * Select a persona. Syncs the foundation persona, persists the choice, and
   * unless `navigate: false` is passed, lands on the persona's home route and
   * raises a toast — the demo's behaviour on a "VIEWING AS" change.
   */
  setPersona: (id: OemPersonaId, options?: { navigate?: boolean }) => void;
  /**
   * fork(oem v1.8): bumped each time `setPersona` moves to a *different*
   * persona. Rehydrating the stored choice on mount does not bump it, so a
   * consumer (the chat rail's "Now viewing as …" line) can tell a user's
   * switch from first paint.
   */
  switchSeq: number;
};

const OemPersonaContext = createContext<OemPersonaContextValue | null>(null);

export function OemPersonaProvider({ children }: { children: ReactNode }) {
  const [personaId, setPersonaId] = useState<OemPersonaId>(DEFAULT_PERSONA);
  const { setPersona: setFoundationPersona } = usePersona();
  const { show } = useToast();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const hydrated = useRef(false);
  const [switchSeq, setSwitchSeq] = useState(0);
  const personaIdRef = useRef(personaId);
  personaIdRef.current = personaId;

  // Rehydrate after mount so first paint is deterministic.
  useEffect(() => {
    const stored = readStored();
    if (stored) setPersonaId(stored);
    hydrated.current = true;
  }, []);

  // Keep the foundation capability lens in step with the OEM job title.
  useEffect(() => {
    setFoundationPersona(OEM_PERSONA_BY_ID[personaId].maps);
  }, [personaId, setFoundationPersona]);

  const setPersona = useCallback(
    (id: OemPersonaId, options?: { navigate?: boolean }) => {
      const next = OEM_PERSONA_BY_ID[id];
      if (id !== personaIdRef.current) setSwitchSeq((n) => n + 1);
      setPersonaId(id);
      writeStored(id);
      if (options?.navigate === false) return;
      navigate(next.home);
      // Demo line 2097: the toast names the org too, so a presenter switching
      // to the parts coordinator hears "· Perth DC", not just a job title.
      show(`Viewing as ${next.name} · ${orgLabel(next, brand.lockup)}`);
    },
    [navigate, show, brand.lockup],
  );

  const value = useMemo<OemPersonaContextValue>(
    () => ({
      persona: OEM_PERSONA_BY_ID[personaId],
      personaId,
      personas: OEM_PERSONAS,
      setPersona,
      switchSeq,
    }),
    [personaId, setPersona, switchSeq],
  );

  return (
    <OemPersonaContext.Provider value={value}>
      {children}
    </OemPersonaContext.Provider>
  );
}

export function useOemPersona(): OemPersonaContextValue {
  const ctx = useContext(OemPersonaContext);
  if (!ctx) {
    throw new Error('useOemPersona must be used inside <OemPersonaProvider>');
  }
  return ctx;
}
